import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './EditorialTeamApi';
import { EditorialTeam, EditorialTeamRequest } from './EditorialTeamTypes';

const editorialTeamKey = getQueryKey('editorialTeam');

export function getEditorialTeamPagedOptions(
  params: PagedParams,
): UseSuspenseQueryOptions<PagedResponse<EditorialTeam>> {
  return {
    queryKey: editorialTeamKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getEditorialTeamOptions(editorialTeamId: Id): UseSuspenseQueryOptions<EditorialTeam> {
  return {
    queryKey: editorialTeamKey.one(editorialTeamId),
    queryFn: () => api.get(editorialTeamId),
  };
}

export function useEditorialTeamEdit(editorialTeamId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: EditorialTeamRequest) => api.edit(editorialTeamId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: editorialTeamKey.root(),
      }),
  });
}

export function useEditorialTeamCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: EditorialTeamRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: editorialTeamKey.root(),
      }),
  });
}

export function useEditorialTeamDelete(editorialTeamId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(editorialTeamId),
    onSuccess: () => invalidateAfterDelete(queryClient, editorialTeamKey, editorialTeamId),
  });
}
